import { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import {
   Mail, MessageSquare, Send, Users, Search, Eye,
  CheckCircle, XCircle, Loader2, RefreshCw,
  CreditCard, BarChart3, History, Settings,
  Smartphone, FileText, X, Plus, Trash2, Edit2,
  Package, Clock,
  Server, Bell, ToggleLeft, ToggleRight, Play,
  Save, ShieldCheck, Calendar, CheckSquare, Square, AlertTriangle
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { useLang } from '@/hooks/useLang'
import { apiService } from '@/services/api'
import { toast } from 'sonner'

// ==================== TYPES ====================
interface MessageTemplate {
  id: number
  name: string
  code: string
  subject: string | null
  body: string
  message_type: 'email' | 'sms'
  category: string
  variables: string | null
  is_active: boolean
}

interface Recipient {
  id: number
  first_name: string
  last_name: string
  email?: string
  phone?: string
  class_name?: string
  role: string
}

interface MessageLog {
  id: number
  message_type: string
  recipient_name: string
  recipient_phone: string | null
  recipient_email: string | null
  subject: string | null
  body: string
  status: string
  sent_at: string
  sms_count: number
  created_at: string
}

interface SmsCredit {
  total_credits: number
  used_credits: number
  remaining_credits: number
}

interface SmsProviderConfig {
  id?: number
  school_id: number
  provider_type: 'orange_cm' | 'mtn_cm' | 'twilio' | 'africas_talking' | 'custom'
  api_key: string
  api_secret: string
  sender_id: string
  username: string
  extra_config: string
  is_active: boolean
  configured?: boolean
}

interface NotificationRule {
  id: number
  school_id: number
  name: string
  event_type: string
  message_type: 'sms' | 'email'
  template_id: number | null
  template_name?: string
  recipient_type: string
  is_active: boolean
  delay_minutes: number
  filters: any
  created_at: string
}

interface SmtpConfigState {
  host: string
  port: number
  username: string
  password: string
  use_tls: boolean
  use_ssl: boolean
  from_name: string
  from_email: string
}

// ==================== HELPER ====================
const extractData = (res: any): any[] => {
  if (Array.isArray(res)) return res
  if (res && typeof res === 'object') {
    if (Array.isArray(res.data)) return res.data
    if (Array.isArray(res.items)) return res.items
  }
  return []
}

const getErrorDetail = (err: any): string => {
  return err?.response?.data?.detail || err?.message || 'Erreur'
}

// Packs SMS (prix adaptés au Cameroun)
const SMS_PACKS = [
  { amount: 100, cost: 2500, label: '100 SMS' },
  { amount: 500, cost: 10000, label: '500 SMS' },
  { amount: 1000, cost: 18000, label: '1 000 SMS' },
  { amount: 5000, cost: 75000, label: '5 000 SMS' },
]

// ==================== COMPONENT ====================
export default function MessagesPage() {
  const location = useLocation()
  const auth = useAuthStore()
  const { lang } = useLang()
  const user = auth.user as any
  const schoolId = user?.school_id || null

  const t = {
    fr: {
      title: 'Communication',
      subtitle: 'Envoi de messages Email et SMS',
      tabEmail: 'Email',
      tabSms: 'SMS',
      tabHistory: 'Historique',
      tabCredits: 'Crédits & Stats',
      tabTemplates: 'Templates',
      tabProvider: 'Fournisseur SMS',
      tabAuto: 'Notifications Auto',
      recipients: 'Destinataires',
      selectRecipients: 'Sélectionner les destinataires',
      selectAll: 'Tout sélectionner',
      deselectAll: 'Tout désélectionner',
      searchRecipient: 'Rechercher...',
      noRecipients: 'Aucun destinataire trouvé',
      messageType: 'Type de message',
      chooseTemplate: 'Choisir un template',
      noTemplate: 'Message personnalisé',
      preview: 'Prévisualiser',
      subject: 'Objet',
      body: 'Message',
      send: 'Envoyer',
      sending: 'Envoi en cours...',
      sendSuccess: 'Message envoyé avec succès',
      sendError: "Erreur lors de l'envoi",
      smsCount: 'SMS nécessaires',
      characters: 'caractères',
      creditsNeeded: 'Crédits nécessaires',
      creditsAvailable: 'Crédits disponibles',
      insufficientCredits: 'Crédits SMS insuffisants',
      previewTitle: 'Prévisualisation',
      close: 'Fermer',
      history: 'Historique des envois',
      noHistory: 'Aucun message envoyé',
      status: 'Statut',
      date: 'Date',
      recipient: 'Destinataire',
      type: 'Type',
      filterByType: 'Filtrer par type',
      filterByStatus: 'Filtrer par statut',
      all: 'Tous',
      sent: 'Envoyé',
      failed: 'Échoué',
      pending: 'En attente',
      stats: 'Statistiques',
      totalSent: 'Total envoyé',
      totalSms: 'SMS envoyés',
      totalEmail: 'Emails envoyés',
      thisMonth: 'Ce mois',
      purchaseCredits: 'Acheter des crédits',
      amount: 'Montant',
      cost: 'Coût',
      buy: 'Acheter',
      templates: 'Templates de messages',
      newTemplate: 'Nouveau template',
      templateName: 'Nom du template',
      templateCode: 'Code',
      templateCategory: 'Catégorie',
      templateBody: 'Corps du message',
      templateSubject: 'Objet (email)',
      templateVariables: 'Variables (JSON)',
      saveTemplate: 'Sauvegarder',
      deleteTemplate: 'Supprimer',
      confirmDelete: 'Supprimer ce template ?',
      emailToTeachers: 'Email aux enseignants',
      smsToParents: 'SMS aux parents',
      selectClass: 'Sélectionner une classe',
      allClasses: 'Toutes les classes',
      sendToClass: 'Envoyer à toute la classe',
      notificationType: 'Type de notification',
      gradeNotification: 'Notes',
      absenceNotification: 'Absences',
      convocationNotification: 'Convocations',
      customNotification: 'Personnalisé',
      selectStudents: 'Sélectionner des élèves',
      bulkSend: 'Envoi groupé',
      individualSend: 'Envoi individuel',
      sendMode: "Mode d'envoi",
      variables: 'Variables',
      addVariable: 'Ajouter une variable',
      variableKey: 'Clé',
      variableValue: 'Valeur',
      refresh: 'Actualiser',
      loading: 'Chargement...',
      providerTitle: 'Configuration du fournisseur SMS',
      providerType: 'Fournisseur',
      apiKey: 'Clé API',
      apiSecret: 'Secret API / Token',
      senderId: 'Sender ID (expéditeur)',
      username: "Nom d'utilisateur",
      extraConfig: 'Configuration extra (JSON)',
      saveProvider: 'Sauvegarder la configuration',
      testProvider: "Tester l'envoi",
      testPhone: 'Numéro de test',
      testMessage: 'Message de test',
      providerConfigured: 'Fournisseur configuré',
      providerNotConfigured: 'Aucun fournisseur configuré',
      lastTest: 'Dernier test',
      autoTitle: 'Règles de notification automatique',
      autoSubtitle: "Envoi automatique lors d'événements (notes, absences...)",
      newRule: 'Nouvelle règle',
      ruleName: 'Nom de la règle',
      eventType: 'Événement déclencheur',
      eventGradeEntered: 'Note saisie',
      eventAbsenceRecorded: 'Absence notée',
      eventConvocationCreated: 'Convocation créée',
      eventDisciplineRecorded: 'Incident disciplinaire',
      eventPaymentReceived: 'Paiement reçu',
      eventStudentRegistered: 'Élève inscrit',
      recipientType: 'Destinataire',
      recipientParent: 'Parent',
      recipientStudent: 'Élève',
      recipientTeacher: 'Enseignant',
      delayMinutes: 'Délai avant envoi (minutes)',
      active: 'Actif',
      inactive: 'Inactif',
      noRules: 'Aucune règle configurée',
      ruleCreated: 'Règle créée',
      ruleUpdated: 'Règle mise à jour',
      ruleDeleted: 'Règle supprimée',
      confirmDeleteRule: 'Supprimer cette règle ?',
      filterMinGrade: 'Note minimale',
      filterClassIds: 'Classes concernées (IDs)',
      // SMTP translations
      smtpTitle: 'Configuration SMTP (Emails)',
      smtpHost: 'Serveur SMTP',
      smtpPort: 'Port',
      smtpUsername: "Nom d'utilisateur / Email",
      smtpPassword: "Mot de passe d'application",
      smtpUseTls: 'Utiliser TLS',
      smtpUseSsl: 'Utiliser SSL',
      smtpFromName: "Nom d'expéditeur",
      smtpFromEmail: "Email d'expéditeur",
      smtpSave: 'Sauvegarder SMTP',
      smtpTest: "Tester l'envoi SMTP",
      smtpTestEmail: 'Email de test',
      smtpTestSubject: 'Objet du test',
      smtpTestBody: 'Corps du message',
      smtpConfigured: 'SMTP configuré',
      smtpNotConfigured: 'SMTP non configuré',
      smtpConfigSaved: 'Configuration SMTP enregistrée',
      smtpTestSent: 'Email de test envoyé !',
      smtpTestFailed: 'Échec envoi SMTP',
      smtpTestRequired: 'Email de test requis',
      smtpPasswordHelp: "Utilisez un 'Mot de passe d'application' Gmail (16 caractères)",
      smtpGmailHint: 'Gmail: smtp.gmail.com:587 avec TLS',
      searchHistory: 'Rechercher dans l\'historique...',
      dateFrom: 'De',
      dateTo: 'À',
      clearFilters: 'Effacer les filtres',
      selectAllLogs: 'Sélectionner tous',
      deleteSelected: 'Supprimer la sélection',
      deleteAll: 'Supprimer tous les logs',
      deleteLog: 'Supprimer ce log',
    },
    en: {
      title: 'Communication',
      subtitle: 'Send Email and SMS messages',
      tabEmail: 'Email',
      tabSms: 'SMS',
      tabHistory: 'History',
      tabCredits: 'Credits & Stats',
      tabTemplates: 'Templates',
      tabProvider: 'SMS Provider',
      tabAuto: 'Auto Notifications',
      recipients: 'Recipients',
      selectRecipients: 'Select recipients',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      searchRecipient: 'Search...',
      noRecipients: 'No recipients found',
      messageType: 'Message type',
      chooseTemplate: 'Choose a template',
      noTemplate: 'Custom message',
      preview: 'Preview',
      subject: 'Subject',
      body: 'Message',
      send: 'Send',
      sending: 'Sending...',
      sendSuccess: 'Message sent successfully',
      sendError: 'Error sending message',
      smsCount: 'SMS needed',
      characters: 'characters',
      creditsNeeded: 'Credits needed',
      creditsAvailable: 'Credits available',
      insufficientCredits: 'Insufficient SMS credits',
      previewTitle: 'Preview',
      close: 'Close',
      history: 'Message history',
      noHistory: 'No messages sent',
      status: 'Status',
      date: 'Date',
      recipient: 'Recipient',
      type: 'Type',
      filterByType: 'Filter by type',
      filterByStatus: 'Filter by status',
      all: 'All',
      sent: 'Sent',
      failed: 'Failed',
      pending: 'Pending',
      stats: 'Statistics',
      totalSent: 'Total sent',
      totalSms: 'SMS sent',
      totalEmail: 'Emails sent',
      thisMonth: 'This month',
      purchaseCredits: 'Purchase credits',
      amount: 'Amount',
      cost: 'Cost',
      buy: 'Buy',
      templates: 'Message templates',
      newTemplate: 'New template',
      templateName: 'Template name',
      templateCode: 'Code',
      templateCategory: 'Category',
      templateBody: 'Message body',
      templateSubject: 'Subject (email)',
      templateVariables: 'Variables (JSON)',
      saveTemplate: 'Save',
      deleteTemplate: 'Delete',
      confirmDelete: 'Delete this template?',
      emailToTeachers: 'Email to teachers',
      smsToParents: 'SMS to parents',
      selectClass: 'Select a class',
      allClasses: 'All classes',
      sendToClass: 'Send to entire class',
      notificationType: 'Notification type',
      gradeNotification: 'Grades',
      absenceNotification: 'Absences',
      convocationNotification: 'Convocations',
      customNotification: 'Custom',
      selectStudents: 'Select students',
      bulkSend: 'Bulk send',
      individualSend: 'Individual send',
      sendMode: 'Send mode',
      variables: 'Variables',
      addVariable: 'Add variable',
      variableKey: 'Key',
      variableValue: 'Value',
      refresh: 'Refresh',
      loading: 'Loading...',
      providerTitle: 'SMS Provider Configuration',
      providerType: 'Provider',
      apiKey: 'API Key',
      apiSecret: 'API Secret / Token',
      senderId: 'Sender ID',
      username: 'Username',
      extraConfig: 'Extra config (JSON)',
      saveProvider: 'Save configuration',
      testProvider: 'Test sending',
      testPhone: 'Test phone number',
      testMessage: 'Test message',
      providerConfigured: 'Provider configured',
      providerNotConfigured: 'No provider configured',
      lastTest: 'Last test',
      autoTitle: 'Automatic Notification Rules',
      autoSubtitle: 'Auto-send on events (grades, absences...)',
      newRule: 'New rule',
      ruleName: 'Rule name',
      eventType: 'Trigger event',
      eventGradeEntered: 'Grade entered',
      eventAbsenceRecorded: 'Absence recorded',
      eventConvocationCreated: 'Convocation created',
      eventDisciplineRecorded: 'Discipline recorded',
      eventPaymentReceived: 'Payment received',
      eventStudentRegistered: 'Student registered',
      recipientType: 'Recipient',
      recipientParent: 'Parent',
      recipientStudent: 'Student',
      recipientTeacher: 'Teacher',
      delayMinutes: 'Delay before sending (minutes)',
      active: 'Active',
      inactive: 'Inactive',
      noRules: 'No rules configured',
      ruleCreated: 'Rule created',
      ruleUpdated: 'Rule updated',
      ruleDeleted: 'Rule deleted',
      confirmDeleteRule: 'Delete this rule?',
      filterMinGrade: 'Minimum grade',
      filterClassIds: 'Concerned classes (IDs)',
      // SMTP translations
      smtpTitle: 'SMTP Configuration (Emails)',
      smtpHost: 'SMTP Host',
      smtpPort: 'Port',
      smtpUsername: 'Username / Email',
      smtpPassword: 'App Password',
      smtpUseTls: 'Use TLS',
      smtpUseSsl: 'Use SSL',
      smtpFromName: 'From Name',
      smtpFromEmail: 'From Email',
      smtpSave: 'Save SMTP',
      smtpTest: 'Test SMTP Send',
      smtpTestEmail: 'Test email',
      smtpTestSubject: 'Test subject',
      smtpTestBody: 'Message body',
      smtpConfigured: 'SMTP configured',
      smtpNotConfigured: 'SMTP not configured',
      smtpConfigSaved: 'SMTP configuration saved',
      smtpTestSent: 'Test email sent!',
      smtpTestFailed: 'SMTP send failed',
      smtpTestRequired: 'Test email required',
      smtpPasswordHelp: "Use a Gmail 'App Password' (16 characters)",
      smtpGmailHint: 'Gmail: smtp.gmail.com:587 with TLS',
      searchHistory: 'Search in history...',
      dateFrom: 'From',
      dateTo: 'To',
      clearFilters: 'Clear filters',
      selectAllLogs: 'Select all',
      deleteSelected: 'Delete selected',
      deleteAll: 'Delete all logs',
      deleteLog: 'Delete this log',
    }
  }[lang]


  // ==================== STATE ====================
  const [activeTab, setActiveTab] = useState<'email' | 'sms' | 'history' | 'credits' | 'templates' | 'provider' | 'auto'>(
    (location.state as any)?.activeTab || 'email'
  )

  // Templates
  const [templates, setTemplates] = useState<MessageTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null)
  const [loadingTemplates, setLoadingTemplates] = useState(false)

  // Recipients
  const [teachers, setTeachers] = useState<Recipient[]>([])
  const [students, setStudents] = useState<Recipient[]>([])
  const [classes, setClasses] = useState<any[]>([])
  const [selectedRecipients, setSelectedRecipients] = useState<number[]>([])
  const [selectedClass, setSelectedClass] = useState<string>('')
  const [recipientSearch, setRecipientSearch] = useState('')
  const [loadingRecipients, setLoadingRecipients] = useState(false)

  // Message
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [variables, setVariables] = useState<Record<string, string>>({})
  const [showPreview, setShowPreview] = useState(false)
  const [previewContent, setPreviewContent] = useState({ subject: '', body: '', sms_length: 0, sms_count: 0 })
  const [sending, setSending] = useState(false)

  // History advanced filters
  const [historySearch, setHistorySearch] = useState('')
  const [historyDateFrom, setHistoryDateFrom] = useState('')
  const [historyDateTo, setHistoryDateTo] = useState('')
  const [selectedLogIds, setSelectedLogIds] = useState<number[]>([])
  const [deletingLogs, setDeletingLogs] = useState(false)
  const canDeleteLogs = user?.role === 'admin' || user?.role === 'principal'

  // SMS specific
  const [notificationType, setNotificationType] = useState('grades')
  const [smsCredits, setSmsCredits] = useState<SmsCredit | null>(null)
  const [sendMode, setSendMode] = useState<'individual' | 'bulk'>('individual')

  // History
  const [logs, setLogs] = useState<MessageLog[]>([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [historyFilter, setHistoryFilter] = useState({ type: '', status: '' })

  // Stats
  const [stats, setStats] = useState<any>(null)
  const [statsLoading, setStatsLoading] = useState(false)

  // Template form
  const [showTemplateForm, setShowTemplateForm] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null)

  // Provider
  const [providerConfig, setProviderConfig] = useState<SmsProviderConfig>({
    school_id: schoolId || 0,
    provider_type: 'orange_cm',
    api_key: '',
    api_secret: '',
    sender_id: '',
    username: '',
    extra_config: '{}',
    is_active: true,
  })
  const [providerLoading, setProviderLoading] = useState(false)
  const [testPhone, setTestPhone] = useState('')
  const [testMessage, setTestMessage] = useState('Test SMS depuis Lycée Bilingue de Baleng')
  const [testSending, setTestSending] = useState(false)

  // Auto notifications
  const [notificationRules, setNotificationRules] = useState<NotificationRule[]>([])
  const [rulesLoading, setRulesLoading] = useState(false)
  const [showRuleForm, setShowRuleForm] = useState(false)
  // ═══ LOGS LOCAUX (fallback si backend vide / tables non créées) ═══
  const [localLogs, setLocalLogs] = useState<MessageLog[]>(() => {
    try {
      const saved = localStorage.getItem('message_local_logs')
      return saved ? JSON.parse(saved) : []
    } catch { return [] }
  })
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null)

  // ═══ SMTP STATE ═══
  const [smtpConfig, setSmtpConfig] = useState<SmtpConfigState>({
    host: 'smtp.gmail.com',
    port: 587,
    username: '',
    password: '',
    use_tls: true,
    use_ssl: false,
    from_name: lang === 'fr' ? 'Lycée Bilingue de Baleng' : 'Lycée Bilingue de Baleng',
    from_email: '',
  })
  const [smtpLoading, setSmtpLoading] = useState(false)
  const [smtpTestEmail, setSmtpTestEmail] = useState('')
  const [smtpTestSubject, setSmtpTestSubject] = useState(
    lang === 'fr' ? 'Test SMTP - Lycée Bilingue de Baleng' : 'SMTP Test - Lycée Bilingue de Baleng'
  )
  const [smtpTestBody, setSmtpTestBody] = useState(
    lang === 'fr'
      ? 'Ceci est un email de test pour vérifier la configuration SMTP.'
      : 'This is a test email to verify SMTP configuration.'
  )
  const [smtpTestSending, setSmtpTestSending] = useState(false)
  const [smtpConfigured, setSmtpConfigured] = useState(false)

  // Détecter si le mot de passe contient des espaces (App Password Gmail mal copié)
  const passwordHasSpaces = smtpConfig.password.includes(' ')

  // ==================== LOAD DATA ====================
  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true)
    try {
      const res = await apiService.get<any>('/messages/templates', {
        params: { school_id: schoolId }
      })
      setTemplates(extractData(res))
    } catch (err) {
      console.error('Error loading templates:', err)
    } finally {
      setLoadingTemplates(false)
    }
  }, [schoolId])

  const loadTeachers = useCallback(async () => {
    setLoadingRecipients(true)
    try {
      const res = await apiService.get<any>('/teachers', { params: { school_id: schoolId } })
      const data = extractData(res)
      setTeachers(data.map((t: any) => ({
        id: t.id,
        first_name: t.first_name || '',
        last_name: t.last_name || '',
        email: t.email || t.user?.email,
        phone: t.phone || t.user?.phone,
        role: 'teacher'
      })))
    } catch (err) {
      console.error('Error loading teachers:', err)
    } finally {
      setLoadingRecipients(false)
    }
  }, [schoolId])

  const loadStudents = useCallback(async () => {
    setLoadingRecipients(true)
    try {
      const res = await apiService.get<any>('/students', { params: { school_id: schoolId } })
      const data = extractData(res)
      setStudents(data.map((s: any) => ({
        id: s.id,
        first_name: s.first_name || '',
        last_name: s.last_name || '',
        email: s.email,
        phone: s.parent_phone || s.guardian_phone,
        class_name: s.class_name,
        role: 'student'
      })))
    } catch (err) {
      console.error('Error loading students:', err)
    } finally {
      setLoadingRecipients(false)
    }
  }, [schoolId])

  const loadClasses = useCallback(async () => {
    try {
      const res = await apiService.get<any>('/classes', { params: { school_id: schoolId } })
      setClasses(extractData(res))
    } catch (err) {
      console.error('Error loading classes:', err)
    }
  }, [schoolId])

  const loadLogs = useCallback(async () => {
    setLogsLoading(true)
    try {
      const params: any = { per_page: 50 }
      if (schoolId) params.school_id = schoolId
      if (historyFilter.type) params.message_type = historyFilter.type
      if (historyFilter.status) params.status = historyFilter.status
      const res = await apiService.get<any>('/messages/logs', { params })
      setLogs(extractData(res.items || res))
    } catch (err) {
      console.error('Error loading logs:', err)
      // Ne pas vider les logs locaux en cas d'erreur backend
    } finally {
      setLogsLoading(false)
    }
  }, [schoolId, historyFilter])

  const loadCredits = useCallback(async () => {
    try {
      const url = schoolId ? `/messages/sms-credits?school_id=${schoolId}` : '/messages/sms-credits'
      const res = await apiService.get<any>(url)
      setSmsCredits(res)
    } catch (err) {
      console.error('Error loading credits:', err)
      // Fallback crédits
      setSmsCredits({ total_credits: 0, used_credits: 0, remaining_credits: 1000 })
    }
  }, [schoolId])

  const loadStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const url = schoolId ? `/messages/stats?school_id=${schoolId}&period=month` : '/messages/stats?period=month'
      const res = await apiService.get<any>(url)
      setStats(res)
    } catch (err) {
      console.error('Error loading stats:', err)
      setStats({ total_sent: localLogs.length, total_sms_sent: 0, total_email_sent: 0, credits_remaining: 1000, daily_stats: [] })
    } finally {
      setStatsLoading(false)
    }
  }, [schoolId, localLogs.length])

  const loadProviderConfig = useCallback(async () => {
    try {
      const url = schoolId ? `/messages/sms-provider?school_id=${schoolId}` : '/messages/sms-provider'
      const res = await apiService.get<any>(url)
      if (res.configured) {
        setProviderConfig({
          school_id: schoolId,
          provider_type: res.provider_type,
          api_key: '',
          api_secret: '',
          sender_id: res.sender_id || '',
          username: res.username || '',
          extra_config: JSON.stringify(res.extra_config || {}),
          is_active: res.is_active,
        })
      }
    } catch (err) {
      console.error('Error loading provider config:', err)
    }
  }, [schoolId])

  const saveProviderConfig = async () => {
    if (!schoolId) return
    setProviderLoading(true)
    try {
      const payload: any = {
        provider_type: providerConfig.provider_type,
        api_key: providerConfig.api_key || undefined,
        api_secret: providerConfig.api_secret || undefined,
        sender_id: providerConfig.sender_id || undefined,
        username: providerConfig.username || undefined,
        extra_config: providerConfig.extra_config || undefined,
      }
      if (schoolId) payload.school_id = schoolId
      await apiService.post('/messages/sms-provider', payload)
      toast.success('Fournisseur SMS configuré')
      loadProviderConfig()
    } catch (err) {
      toast.error(getErrorDetail(err))
    } finally {
      setProviderLoading(false)
    }
  }

  const handleTestSms = async () => {
    if (!schoolId || !testPhone) {
      toast.error(lang === 'fr' ? 'Numéro de test requis' : 'Test phone required')
      return
    }
    setTestSending(true)
    try {
      const payload: any = { phone: testPhone, message: testMessage }
      if (schoolId) payload.school_id = schoolId
      const res = await apiService.post<any>('/messages/sms-provider/test', payload)
      if (res.status === 'sent') {
        toast.success('SMS de test envoyé !')
      } else {
        toast.error(`Échec: ${res.error || 'Inconnu'}`)
      }
    } catch (err) {
      toast.error(getErrorDetail(err))
    } finally {
      setTestSending(false)
    }
  }

  const loadNotificationRules = useCallback(async () => {
    setRulesLoading(true)
    try {
      const url = schoolId ? `/messages/notification-rules?school_id=${schoolId}` : '/messages/notification-rules'
      const res = await apiService.get<any>(url)
      setNotificationRules(extractData(res))
    } catch (err) {
      console.error('Error loading rules:', err)
    } finally {
      setRulesLoading(false)
    }
  }, [schoolId])

  // ═══ SMTP LOAD / SAVE / TEST ═══
  const loadSmtpConfig = useCallback(async () => {
    try {
      const url = schoolId ? `/messages/smtp-config?school_id=${schoolId}` : '/messages/smtp-config'
      const res = await apiService.get<any>(url)
      if (res.configured) {
        setSmtpConfigured(true)
        setSmtpConfig(prev => ({
          ...prev,
          host: res.host || 'smtp.gmail.com',
          port: res.port || 587,
          username: res.username || '',
          password: '', // Ne jamais pré-remplir le mot de passe
          use_tls: res.use_tls !== false,
          use_ssl: res.use_ssl || false,
          from_name: res.from_name || (lang === 'fr' ? 'Lycée Bilingue de Baleng' : 'Lycée Bilingue de Baleng'),
          from_email: res.from_email || '',
        }))
      } else {
        setSmtpConfigured(false)
      }
    } catch (err) {
      console.error('Error loading SMTP config:', err)
      setSmtpConfigured(false)
    }
  }, [schoolId, lang])

  const saveSmtpConfig = async () => {
    if (!schoolId) {
      toast.error(lang === 'fr' ? 'School ID requis' : 'School ID required')
      return
    }
    // Nettoyer le mot de passe (retirer les espaces des App Passwords Gmail)
    const cleanPassword = smtpConfig.password.replace(/\s/g, '')
    if (!smtpConfig.username || !cleanPassword) {
      toast.error(lang === 'fr' ? 'Email et mot de passe requis' : 'Email and password required')
      return
    }
    setSmtpLoading(true)
    try {
      const payload = { ...smtpConfig, password: cleanPassword, school_id: schoolId }
      await apiService.post('/messages/smtp-config', payload)
      // Mettre à jour le state local avec le mot de passe nettoyé
      setSmtpConfig(prev => ({ ...prev, password: cleanPassword }))
      toast.success(t.smtpConfigSaved)
      setSmtpConfigured(true)
      loadSmtpConfig()
    } catch (err) {
      toast.error(getErrorDetail(err))
    } finally {
      setSmtpLoading(false)
    }
  }

  const handleTestSmtp = async () => {
    if (!smtpTestEmail) {
      toast.error(t.smtpTestRequired)
      return
    }
    setSmtpTestSending(true)
    try {
      const payload = {
        to_email: smtpTestEmail,
        subject: smtpTestSubject,
        body: smtpTestBody,
      }
      const url = schoolId ? `/messages/smtp-config/test?school_id=${schoolId}` : '/messages/smtp-config/test'
      const res = await apiService.post<any>(url, payload)
      if (res.status === 'sent') {
        toast.success(t.smtpTestSent)
      } else {
        toast.error(`${t.smtpTestFailed}: ${res.error || 'Unknown'}`)
      }
    } catch (err) {
      toast.error(getErrorDetail(err))
    } finally {
      setSmtpTestSending(false)
    }
  }

  const handleCreateRule = async (ruleData: any) => {
    try {
      const payload = { ...ruleData }
      if (schoolId) payload.school_id = schoolId
      await apiService.post('/messages/notification-rules', payload)
      toast.success(t.ruleCreated)
      setShowRuleForm(false)
      setEditingRule(null)
      loadNotificationRules()
    } catch (err) {
      toast.error(getErrorDetail(err))
    }
  }

  // FIX: handleUpdateRule pour l'édition
  const handleUpdateRule = async (ruleId: number, ruleData: any) => {
    try {
      await apiService.put(`/messages/notification-rules/${ruleId}`, ruleData)
      toast.success(t.ruleUpdated)
      setShowRuleForm(false)
      setEditingRule(null)
      loadNotificationRules()
    } catch (err) {
      toast.error(getErrorDetail(err))
    }
  }

  const handleToggleRule = async (rule: NotificationRule) => {
    try {
      await apiService.put(`/messages/notification-rules/${rule.id}`, {
        is_active: !rule.is_active
      })
      toast.success(!rule.is_active ? t.active : t.inactive)
      loadNotificationRules()
    } catch (err) {
      toast.error(getErrorDetail(err))
    }
  }

  const handleDeleteRule = async (id: number) => {
    if (!confirm(t.confirmDeleteRule)) return
    try {
      await apiService.delete(`/messages/notification-rules/${id}`)
      toast.success(t.ruleDeleted)
      loadNotificationRules()
    } catch (err) {
      toast.error(getErrorDetail(err))
    }
  }

  useEffect(() => {
    loadTemplates()
    loadClasses()
    loadCredits()
    loadProviderConfig()
    loadNotificationRules()
    loadSmtpConfig()
  }, [loadTemplates, loadClasses, loadCredits, loadProviderConfig, loadNotificationRules, loadSmtpConfig])

  useEffect(() => {
    if (activeTab === 'email') loadTeachers()
    if (activeTab === 'sms') loadStudents()
    if (activeTab === 'history') loadLogs()
    if (activeTab === 'credits') { loadCredits(); loadStats() }
    if (activeTab === 'provider') { loadProviderConfig(); loadSmtpConfig() }
    if (activeTab === 'auto') loadNotificationRules()
  }, [activeTab, loadTeachers, loadStudents, loadLogs, loadCredits, loadStats, loadProviderConfig, loadNotificationRules, loadSmtpConfig])


  // ==================== HELPERS ====================
  const filteredRecipients = useMemo(() => {
    const list = activeTab === 'email' ? teachers : students
    if (!recipientSearch) return list
    const q = recipientSearch.toLowerCase()
    return list.filter(r =>
      `${r.first_name} ${r.last_name}`.toLowerCase().includes(q) ||
      (r.email && r.email.toLowerCase().includes(q)) ||
      (r.phone && r.phone.includes(q)) ||
      (r.class_name && r.class_name.toLowerCase().includes(q))
    )
  }, [activeTab, teachers, students, recipientSearch])

  const currentTemplateList = useMemo(() => {
    return templates.filter(t => t.message_type === activeTab)
  }, [templates, activeTab])

  const calculateSmsCount = (text: string) => {
    const len = text.length
    return Math.max(1, Math.ceil(len / 160))
  }

  // ==================== HANDLERS ====================
  const handleTemplateChange = (templateId: string) => {
    if (!templateId) {
      setSelectedTemplate(null)
      setSubject('')
      setBody('')
      setVariables({})
      return
    }
    const tmpl = templates.find(t => t.id.toString() === templateId)
    if (tmpl) {
      setSelectedTemplate(tmpl)
      setSubject(tmpl.subject || '')
      setBody(tmpl.body)
      if (tmpl.variables) {
        try {
          const vars: string[] = JSON.parse(tmpl.variables)
          const initialVars: Record<string, string> = {}
          vars.forEach(v => { initialVars[v] = '' })
          setVariables(initialVars)
        } catch {
          setVariables({})
        }
      } else {
        setVariables({})
      }
    }
  }

  const handlePreview = async () => {
    if (!selectedTemplate && !body) return
    try {
      const recipient = filteredRecipients.find(r => selectedRecipients.includes(r.id))
      const res = await apiService.post<any>('/messages/preview', {
        template_id: selectedTemplate?.id,
        variables: variables,
        recipient_name: recipient ? `${recipient.first_name} ${recipient.last_name}` : undefined
      })
      setPreviewContent(res)
      setShowPreview(true)
    } catch (err) {
      toast.error(getErrorDetail(err))
    }
  }

  const handleSend = async () => {
    if (selectedRecipients.length === 0 && sendMode === 'individual') {
      toast.error(lang === 'fr' ? 'Sélectionnez au moins un destinataire' : 'Select at least one recipient')
      return
    }
    if (!body && !selectedTemplate) {
      toast.error(lang === 'fr' ? 'Rédigez un message ou choisissez un template' : 'Write a message or choose a template')
      return
    }

    setSending(true)
    try {
      if (activeTab === 'email') {
        await apiService.post('/messages/send', {
          message_type: 'email',
          recipient_ids: selectedRecipients,
          recipient_role: 'teacher',
          template_id: selectedTemplate?.id,
          subject,
          body,
          variables,
          ...(schoolId ? { school_id: schoolId } : {})
        })
      } else {
        if (sendMode === 'individual') {
          await apiService.post('/messages/send', {
            message_type: 'sms',
            recipient_ids: selectedRecipients,
            recipient_role: 'parent',
            template_id: selectedTemplate?.id,
            body,
            variables,
            ...(schoolId ? { school_id: schoolId } : {})
          })
        } else {
          await apiService.post('/messages/send-bulk-sms', {
            class_ids: selectedClass ? [parseInt(selectedClass)] : undefined,
            template_id: selectedTemplate?.id,
            body,
            variables,
            ...(schoolId ? { school_id: schoolId } : {}),
            notification_type: notificationType
          })
        }
      }
      toast.success(t.sendSuccess)

      // Note: l'email est enregistré mais pas envoyé via SMTP réel
      // Configurez un service SMTP (SendGrid, AWS SES, etc.) pour l'envoi réel
      if (activeTab === 'email') {
        toast.info("Email enregistré. Configurez un service SMTP pour l'envoi réel.", { duration: 4000 })
      }

      // Ajouter immédiatement à l'historique local
      const now = new Date().toISOString()
      const newLog: MessageLog = {
        id: Date.now(),
        message_type: activeTab === 'email' ? 'email' : 'sms',
        recipient_name: activeTab === 'email'
          ? teachers.filter(t => selectedRecipients.includes(t.id)).map(t => `${t.first_name} ${t.last_name}`).join(', ')
          : students.filter(s => selectedRecipients.includes(s.id)).map(s => `${s.first_name} ${s.last_name}`).join(', '),
        recipient_phone: activeTab === 'sms' ? students.filter(s => selectedRecipients.includes(s.id)).map(s => s.phone).filter(Boolean).join(', ') : null,
        recipient_email: activeTab === 'email' ? teachers.filter(t => selectedRecipients.includes(t.id)).map(t => t.email).filter(Boolean).join(', ') : null,
        subject: subject || null,
        body: body,
        status: 'sent',
        sent_at: now,
        sms_count: activeTab === 'sms' ? calculateSmsCount(body) : 0,
        created_at: now,
      }
      setLocalLogs(prev => [newLog, ...prev])

      setSelectedRecipients([])
      setSubject('')
      setBody('')
      setVariables({})
      setSelectedTemplate(null)
      loadCredits()
      loadLogs()
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      if (detail?.includes('insuffisants') || detail?.includes('insufficient')) {
        toast.error(t.insufficientCredits)
      } else {
        toast.error(getErrorDetail(err) || t.sendError)
      }
    } finally {
      setSending(false)
    }
  }

  const handlePurchaseCredits = async (amount: number, cost: number) => {
    try {
      const payload: any = { amount, cost, description: `Achat de ${amount} crédits SMS` }
      if (schoolId) payload.school_id = schoolId
      await apiService.post('/messages/sms-credits/purchase', payload)
      toast.success(`${amount} crédits SMS ajoutés`)
      loadCredits()
    } catch (err) {
      toast.error(getErrorDetail(err))
    }
  }

  const handleSaveTemplate = async (templateData: any) => {
    try {
      if (editingTemplate) {
        await apiService.put(`/messages/templates/${editingTemplate.id}`, templateData)
        toast.success('Template mis à jour')
      } else {
        const payload = { ...templateData }
        if (schoolId) payload.school_id = schoolId
        await apiService.post('/messages/templates', payload)
        toast.success('Template créé')
      }
      setShowTemplateForm(false)
      setEditingTemplate(null)
      loadTemplates()
    } catch (err) {
      toast.error(getErrorDetail(err))
    }
  }

  const handleDeleteTemplate = async (id: number) => {
    if (!confirm(t.confirmDelete)) return
    try {
      await apiService.delete(`/messages/templates/${id}`)
      toast.success('Template supprimé')
      loadTemplates()
    } catch (err) {
      toast.error(getErrorDetail(err))
    }
  }

  const toggleLogSelection = (logId: number) => {
    setSelectedLogIds(prev => 
      prev.includes(logId) ? prev.filter(id => id !== logId) : [...prev, logId]
    )
  }

  const toggleAllLogs = (logIds: number[]) => {
    if (selectedLogIds.length === logIds.length) {
      setSelectedLogIds([])
    } else {
      setSelectedLogIds(logIds)
    }
  }

  const handleDeleteLog = async (id: number) => {
    setDeletingLogs(true)
    try {
      await apiService.delete(`/messages/logs/${id}`)
      toast.success('Log supprimé')
      setLocalLogs(prev => prev.filter(l => l.id !== id))
      loadLogs()
    } catch (err) {
      toast.error(getErrorDetail(err))
    } finally {
      setDeletingLogs(false)
    }
  }

  const handleDeleteSelectedLogs = async () => {
    if (selectedLogIds.length === 0) return
    setDeletingLogs(true)
    try {
      await Promise.all(selectedLogIds.map(id => apiService.delete(`/messages/logs/${id}`)))
      toast.success(`${selectedLogIds.length} logs supprimés`)
      setLocalLogs(prev => prev.filter(l => !selectedLogIds.includes(l.id)))
      setSelectedLogIds([])
      loadLogs()
    } catch (err) {
      toast.error(getErrorDetail(err))
    } finally {
      setDeletingLogs(false)
    }
  }

  const handleDeleteAllLogs = async () => {
    if (!confirm(lang === 'fr' ? 'Supprimer TOUS les logs ?' : 'Delete ALL logs?')) return
    setDeletingLogs(true)
    try {
      await apiService.delete('/messages/logs')
      toast.success('Tous les logs supprimés')
      setLocalLogs([])
      setSelectedLogIds([])
      loadLogs()
    } catch (err) {
      toast.error(getErrorDetail(err))
    } finally {
      setDeletingLogs(false)
    }
  }

  const clearHistoryFilters = () => {
    setHistorySearch('')
    setHistoryDateFrom('')
    setHistoryDateTo('')
    setHistoryFilter({ type: '', status: '' })
    loadLogs()
  }

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-7 h-7 text-[#1e3a8a]" />
              {t.title}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
          </div>
          {smsCredits && (
            <div className="flex items-center gap-3">
              <div className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <span className="text-blue-600 font-medium">{t.creditsAvailable}: </span>
                <span className="text-blue-800 font-bold">{smsCredits.remaining_credits}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit flex-wrap">
          {[
            { key: 'email' as const, label: t.tabEmail, icon: Mail },
            { key: 'sms' as const, label: t.tabSms, icon: Smartphone },
            { key: 'history' as const, label: t.tabHistory, icon: History },
            { key: 'credits' as const, label: t.tabCredits, icon: CreditCard },
            { key: 'templates' as const, label: t.tabTemplates, icon: FileText },
            { key: 'provider' as const, label: t.tabProvider, icon: Server },
            { key: 'auto' as const, label: t.tabAuto, icon: Bell },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-white text-[#1e3a8a] shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>


      <div className="p-6">
        {/* ==================== EMAIL TAB ==================== */}
        {activeTab === 'email' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recipients Panel */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#1e3a8a]" />
                {t.recipients}
              </h3>
              <div className="relative mb-3">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder={t.searchRecipient}
                  value={recipientSearch}
                  onChange={(e) => setRecipientSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a]"
                />
              </div>
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setSelectedRecipients(filteredRecipients.map(r => r.id))}
                  className="text-xs text-[#1e3a8a] hover:underline"
                >
                  {t.selectAll}
                </button>
                <button
                  onClick={() => setSelectedRecipients([])}
                  className="text-xs text-gray-500 hover:underline"
                >
                  {t.deselectAll}
                </button>
              </div>
              <div className="max-h-[400px] overflow-y-auto space-y-1">
                {loadingRecipients ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : filteredRecipients.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">{t.noRecipients}</p>
                ) : (
                  filteredRecipients.map(recipient => (
                    <label
                      key={recipient.id}
                      className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                        selectedRecipients.includes(recipient.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRecipients.includes(recipient.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRecipients(prev => [...prev, recipient.id])
                          } else {
                            setSelectedRecipients(prev => prev.filter(id => id !== recipient.id))
                          }
                        }}
                        className="w-4 h-4 text-[#1e3a8a] rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {recipient.first_name} {recipient.last_name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{recipient.email}</p>
                      </div>
                    </label>
                  ))
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  {selectedRecipients.length} {lang === 'fr' ? 'sélectionné(s)' : 'selected'} / {filteredRecipients.length}
                </p>
              </div>
            </div>

            {/* Message Panel */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#1e3a8a]" />
                {t.emailToTeachers}
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.chooseTemplate}</label>
                <select
                  value={selectedTemplate?.id || ''}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                >
                  <option value="">{t.noTemplate}</option>
                  {currentTemplateList.map(tmpl => (
                    <option key={tmpl.id} value={tmpl.id}>{tmpl.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.subject}</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Objet du message..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.body}</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={8}
                  placeholder="Rédigez votre message..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] resize-none"
                />
              </div>

              {Object.keys(variables).length > 0 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-semibold text-gray-600 mb-2">{t.variables}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(variables).map(([key, value]) => (
                      <div key={key}>
                        <label className="text-xs text-gray-500">{key}</label>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => setVariables(prev => ({ ...prev, [key]: e.target.value }))}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                          placeholder={`{{${key}}}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={handlePreview}
                  disabled={!body}
                  className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50"
                >
                  <Eye className="w-4 h-4" />
                  {t.preview}
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || selectedRecipients.length === 0}
                  className="px-4 py-2.5 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center gap-2 disabled:opacity-50"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? t.sending : t.send}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ==================== SMS TAB ==================== */}
        {activeTab === 'sms' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Settings className="w-4 h-4 text-[#1e3a8a]" />
                {t.messageType}
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.sendMode}</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSendMode('individual')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      sendMode === 'individual' ? 'bg-[#1e3a8a] text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {t.individualSend}
                  </button>
                  <button
                    onClick={() => setSendMode('bulk')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      sendMode === 'bulk' ? 'bg-[#1e3a8a] text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {t.bulkSend}
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.notificationType}</label>
                <select
                  value={notificationType}
                  onChange={(e) => setNotificationType(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
                >
                  <option value="grades">{t.gradeNotification}</option>
                  <option value="absence">{t.absenceNotification}</option>
                  <option value="convocation">{t.convocationNotification}</option>
                  <option value="custom">{t.customNotification}</option>
                </select>
              </div>

              {sendMode === 'bulk' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.selectClass}</label>
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="">{t.allClasses}</option>
                    {classes.map((c: any) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {smsCredits && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-blue-600 font-medium">{t.creditsAvailable}</span>
                    <span className="text-sm font-bold text-blue-800">{smsCredits.remaining_credits}</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all"
                      style={{ width: `${Math.min((smsCredits.used_credits / Math.max(smsCredits.total_credits, 1)) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {sendMode === 'individual' && (
                <>
                  <div className="relative mb-3 mt-4">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder={t.searchRecipient}
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>
                  <div className="max-h-[250px] overflow-y-auto space-y-1">
                    {filteredRecipients.map(recipient => (
                      <label
                        key={recipient.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedRecipients.includes(recipient.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRecipients.includes(recipient.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRecipients(prev => [...prev, recipient.id])
                            } else {
                              setSelectedRecipients(prev => prev.filter(id => id !== recipient.id))
                            }
                          }}
                          className="w-4 h-4 text-[#1e3a8a] rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {recipient.first_name} {recipient.last_name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{recipient.phone || '—'}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-[#1e3a8a]" />
                {t.smsToParents}
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.chooseTemplate}</label>
                <select
                  value={selectedTemplate?.id || ''}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
                >
                  <option value="">{t.noTemplate}</option>
                  {currentTemplateList.map(tmpl => (
                    <option key={tmpl.id} value={tmpl.id}>{tmpl.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.body}</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={5}
                  maxLength={480}
                  placeholder="Rédigez votre SMS..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] resize-none"
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-400">
                    {body.length} {t.characters}
                  </span>
                  <span className="text-xs text-gray-400">
                    {t.smsCount}: <span className="font-medium text-[#1e3a8a]">{calculateSmsCount(body)}</span>
                  </span>
                </div>
              </div>

              {Object.keys(variables).length > 0 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-semibold text-gray-600 mb-2">{t.variables}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(variables).map(([key, value]) => (
                      <div key={key}>
                        <label className="text-xs text-gray-500">{key}</label>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => setVariables(prev => ({ ...prev, [key]: e.target.value }))}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={handlePreview}
                  disabled={!body}
                  className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50"
                >
                  <Eye className="w-4 h-4" />
                  {t.preview}
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || (sendMode === 'individual' && selectedRecipients.length === 0) || (sendMode === 'bulk' && !selectedClass)}
                  className="px-4 py-2.5 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center gap-2 disabled:opacity-50"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? t.sending : t.send}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ==================== HISTORY TAB ==================== */}

       {activeTab === 'history' && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
            {/* Header avec filtres avancés */}
            <div className="flex flex-col gap-4 mb-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <History className="w-5 h-5 text-[#1e3a8a]" />
                {t.history}
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                {/* Filtres */}
                <div className="relative">
                    <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                    type="text"
                    placeholder={t.searchHistory}
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm w-40 focus:ring-2 focus:ring-[#1e3a8a]"
                    />
                </div>
                <input
                    type="date"
                    value={historyDateFrom}
                    onChange={(e) => setHistoryDateFrom(e.target.value)}
                    className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                    title={t.dateFrom}
                />
                <input
                    type="date"
                    value={historyDateTo}
                    onChange={(e) => setHistoryDateTo(e.target.value)}
                    className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                    title={t.dateTo}
                />
                <select
                    value={historyFilter.type}
                    onChange={(e) => setHistoryFilter(prev => ({ ...prev, type: e.target.value }))}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                >
                    <option value="">{t.filterByType}</option>
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                </select>
                <select
                    value={historyFilter.status}
                    onChange={(e) => setHistoryFilter(prev => ({ ...prev, status: e.target.value }))}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                >
                    <option value="">{t.filterByStatus}</option>
                    <option value="sent">{t.sent}</option>
                    <option value="failed">{t.failed}</option>
                    <option value="pending">{t.pending}</option>
                </select>
                <button onClick={clearHistoryFilters} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200">
                    {t.clearFilters}
                </button>
                <button onClick={loadLogs} className="p-1.5 hover:bg-gray-100 rounded-lg">
                    <RefreshCw className="w-4 h-4 text-gray-500" />
                </button>
                </div>
            </div>

            {/* Barre de suppression bulk */}
            {canDeleteLogs && (
                <div className="flex items-center justify-between p-2 bg-red-50 border border-red-100 rounded-lg">
                <div className="flex items-center gap-3">
                    <button
                    onClick={() => toggleAllLogs([...localLogs, ...logs].map(l => l.id))}
                    className="text-xs text-[#1e3a8a] hover:underline flex items-center gap-1"
                    >
                    {selectedLogIds.length === [...localLogs, ...logs].length ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                    {selectedLogIds.length > 0 ? `${selectedLogIds.length} sélectionné(s)` : t.selectAllLogs}
                    </button>
                </div>
                <div className="flex gap-2">
                    {selectedLogIds.length > 0 && (
                    <button
                        onClick={handleDeleteSelectedLogs}
                        disabled={deletingLogs}
                        className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 flex items-center gap-1.5 disabled:opacity-50"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                        {t.deleteSelected}
                    </button>
                    )}
                    <button
                    onClick={handleDeleteAllLogs}
                    disabled={deletingLogs}
                    className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 flex items-center gap-1.5 disabled:opacity-50"
                    >
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {t.deleteAll}
                    </button>
                </div>
                </div>
            )}
            </div>

            {logsLoading ? (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
            ) : ([...localLogs, ...logs].length === 0) ? (
            <div className="text-center py-12">
                <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm text-gray-500">{t.noHistory}</p>
            </div>
            ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                    {canDeleteLogs && (
                        <th className="px-2 py-3 w-8">
                        <input
                            type="checkbox"
                            checked={selectedLogIds.length > 0 && selectedLogIds.length === [...localLogs, ...logs].length}
                            onChange={() => toggleAllLogs([...localLogs, ...logs].map(l => l.id))}
                            className="w-4 h-4 rounded"
                        />
                        </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">{t.type}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">{t.recipient}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">{t.subject}</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase">SMS</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase">{t.status}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">{t.date}</th>
                    {canDeleteLogs && <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase">{t.deleteLog}</th>}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {[...localLogs, ...logs].map(log => (
                    <tr key={log.id} className={`hover:bg-gray-50 ${selectedLogIds.includes(log.id) ? 'bg-blue-50/50' : ''}`}>
                        {canDeleteLogs && (
                        <td className="px-2 py-3">
                            <input
                            type="checkbox"
                            checked={selectedLogIds.includes(log.id)}
                            onChange={() => toggleLogSelection(log.id)}
                            className="w-4 h-4 rounded"
                            />
                        </td>
                        )}
                        <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            log.message_type === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                            {log.message_type === 'email' ? <Mail className="w-3 h-3" /> : <Smartphone className="w-3 h-3" />}
                            {log.message_type.toUpperCase()}
                        </span>
                        </td>
                        <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{log.recipient_name}</p>
                        <p className="text-xs text-gray-500">{log.recipient_phone || log.recipient_email}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate">
                        {log.subject || log.body.substring(0, 50)}...
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">{log.sms_count}</td>
                        <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            log.status === 'sent' ? 'bg-emerald-100 text-emerald-700' :
                            log.status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                        }`}>
                            {log.status === 'sent' ? <CheckCircle className="w-3 h-3" /> :
                            log.status === 'failed' ? <XCircle className="w-3 h-3" /> :
                            <Clock className="w-3 h-3" />}
                            {log.status}
                        </span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-500 text-xs">
                        {new Date(log.created_at).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US')}
                        </td>
                        {canDeleteLogs && (
                        <td className="px-4 py-3 text-center">
                            <button
                            onClick={() => handleDeleteLog(log.id)}
                            disabled={deletingLogs}
                            className="p-1 hover:bg-red-50 rounded text-red-500 disabled:opacity-50"
                            title={t.deleteLog}
                            >
                            <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </td>
                        )}
                    </tr>
                    ))}
                </tbody>
                </table>
            </div>
            )}
        </div>
        )}

        {/* ==================== CREDITS TAB (PRIX ADAPTÉS) ==================== */}
        {activeTab === 'credits' && (
          <div className="space-y-6">
            {/* Stats calculées depuis les logs locaux */}
            {(() => {
              const emailLogs = localLogs.filter(l => l.message_type === 'email')
              const smsLogs = localLogs.filter(l => l.message_type === 'sms')
              const failedLogs = localLogs.filter(l => l.status === 'failed')
              const total = localLogs.length
              const successRate = total ? Math.round(((total - failedLogs.length) / total) * 100) : 100
              return (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-2">
                      <Send className="w-5 h-5 text-[#1e3a8a]" />
                      <span className="text-xs text-gray-400">{lang === 'fr' ? 'Session' : 'Session'}</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{total}</p>
                    <p className="text-sm text-gray-500">{t.totalSent}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-2">
                      <Smartphone className="w-5 h-5 text-green-600" />
                      <span className="text-xs text-gray-400">{lang === 'fr' ? 'Session' : 'Session'}</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{smsLogs.length}</p>
                    <p className="text-sm text-gray-500">{t.totalSms}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-2">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <span className="text-xs text-gray-400">{lang === 'fr' ? 'Session' : 'Session'}</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{emailLogs.length}</p>
                    <p className="text-sm text-gray-500">{t.totalEmail}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-2">
                      <CreditCard className="w-5 h-5 text-amber-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{smsCredits?.remaining_credits || 1000}</p>
                    <p className="text-sm text-gray-500">{t.creditsAvailable}</p>
                  </div>
                </div>
              )
            })()}

            {statsLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">{t.loading}</span>
              </div>
            )}
            {/* Stats backend (mensuelles) */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <Send className="w-5 h-5 text-[#1e3a8a]" />
                  <span className="text-xs text-gray-400">{t.thisMonth}</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_sent || 0}</p>
                <p className="text-sm text-gray-500">{t.totalSent} (backend)</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <Smartphone className="w-5 h-5 text-green-600" />
                  <span className="text-xs text-gray-400">{t.thisMonth}</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_sms_sent || 0}</p>
                <p className="text-sm text-gray-500">{t.totalSms} (backend)</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <span className="text-xs text-gray-400">{t.thisMonth}</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_email_sent || 0}</p>
                <p className="text-sm text-gray-500">{t.totalEmail} (backend)</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{smsCredits?.remaining_credits || 0}</p>
                <p className="text-sm text-gray-500">{t.creditsAvailable}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-[#1e3a8a]" />
                {t.purchaseCredits}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {SMS_PACKS.map(pkg => (
                  <div key={pkg.amount} className="border border-gray-200 rounded-xl p-4 hover:border-[#1e3a8a] transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <Smartphone className="w-5 h-5 text-[#1e3a8a]" />
                      <span className="text-lg font-bold text-gray-900">{pkg.amount}</span>
                      <span className="text-sm text-gray-500">SMS</span>
                    </div>
                    <p className="text-2xl font-bold text-[#1e3a8a] mb-3">{pkg.cost.toLocaleString()} FCFA</p>
                    <p className="text-xs text-gray-400 mb-3">~{Math.round(pkg.cost / pkg.amount)} FCFA/SMS</p>
                    <button
                      onClick={() => handlePurchaseCredits(pkg.amount, pkg.cost)}
                      className="w-full px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e]"
                    >
                      {t.buy}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {stats?.daily_stats && stats.daily_stats.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#1e3a8a]" />
                  {t.stats}
                </h3>
                <div className="h-64 flex items-end gap-2">
                  {stats.daily_stats.map((day: any, i: number) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-blue-100 rounded-t" style={{ height: `${Math.min((day.count / Math.max(...stats.daily_stats.map((d: any) => d.count))) * 200, 200)}px` }} />
                      <span className="text-[10px] text-gray-500 rotate-45 origin-left translate-y-2">{day.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== TEMPLATES TAB ==================== */}
        {activeTab === 'templates' && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1e3a8a]" />
                {t.templates}
              </h3>
              <button
                onClick={() => { setEditingTemplate(null); setShowTemplateForm(true) }}
                className="px-3 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t.newTemplate}
              </button>
            </div>

            {loadingTemplates ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(tmpl => (
                  <div key={tmpl.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          tmpl.message_type === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {tmpl.message_type.toUpperCase()}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">{tmpl.category}</span>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => { setEditingTemplate(tmpl); setShowTemplateForm(true) }}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <Edit2 className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(tmpl.id)}
                          className="p-1 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      </div>
                    </div>
                    <h4 className="font-medium text-gray-900 mb-1">{tmpl.name}</h4>
                    <p className="text-xs text-gray-500 mb-2">Code: {tmpl.code}</p>
                    <p className="text-sm text-gray-600 line-clamp-3">{tmpl.body}</p>
                    {tmpl.variables && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {JSON.parse(tmpl.variables).map((v: string) => (
                          <span key={v} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{`{{${v}}}`}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== PROVIDER TAB (SMS + SMTP) ==================== */}
        {activeTab === 'provider' && (
          <div className="space-y-6">
            {/* Ligne 1 : Config SMS | Test SMS */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Server className="w-5 h-5 text-[#1e3a8a]" />
                  {t.providerTitle}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.providerType}</label>
                    <select
                      value={providerConfig.provider_type}
                      onChange={(e) => setProviderConfig(prev => ({ ...prev, provider_type: e.target.value as any }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                    >
                      <option value="orange_cm">Orange Cameroun</option>
                      <option value="mtn_cm">MTN Cameroun</option>
                      <option value="twilio">Twilio</option>
                      <option value="africas_talking">Africa's Talking</option>
                      <option value="custom">Custom / Autre</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.senderId}</label>
                    <input
                      type="text"
                      value={providerConfig.sender_id}
                      onChange={(e) => setProviderConfig(prev => ({ ...prev, sender_id: e.target.value }))}
                      placeholder="LYCEE_BALENG"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.apiKey}</label>
                    <input
                      type="password"
                      value={providerConfig.api_key}
                      onChange={(e) => setProviderConfig(prev => ({ ...prev, api_key: e.target.value }))}
                      placeholder="Votre clé API..."
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.apiSecret}</label>
                    <input
                      type="password"
                      value={providerConfig.api_secret}
                      onChange={(e) => setProviderConfig(prev => ({ ...prev, api_secret: e.target.value }))}
                      placeholder="Votre secret / token..."
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.username}</label>
                    <input
                      type="text"
                      value={providerConfig.username}
                      onChange={(e) => setProviderConfig(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="Nom d'utilisateur (Twilio SID, Africa's Talking username...)"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.extraConfig}</label>
                    <textarea
                      value={providerConfig.extra_config}
                      onChange={(e) => setProviderConfig(prev => ({ ...prev, extra_config: e.target.value }))}
                      rows={3}
                      placeholder='{"endpoint": "https://...", "method": "POST"}'
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-mono resize-none"
                    />
                  </div>

                  <button
                    onClick={saveProviderConfig}
                    disabled={providerLoading}
                    className="w-full px-4 py-2.5 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {providerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {t.saveProvider}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Play className="w-5 h-5 text-[#1e3a8a]" />
                  {t.testProvider}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.testPhone}</label>
                    <input
                      type="text"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      placeholder="2376XXXXXXXX"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.testMessage}</label>
                    <textarea
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm resize-none"
                    />
                  </div>

                  <button
                    onClick={handleTestSms}
                    disabled={testSending || !testPhone}
                    className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {testSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {t.testProvider}
                  </button>

                  <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-500">
                    <p className="font-medium text-gray-700 mb-1">Configuration actuelle:</p>
                    <p>Fournisseur: {providerConfig.provider_type || '—'}</p>
                    <p>Sender ID: {providerConfig.sender_id || '—'}</p>
                    <p>API Key: {providerConfig.api_key ? '***' + providerConfig.api_key.slice(-4) : '—'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ═══ SÉPARATEUR SMTP ═══ */}
            <div className="border-t border-gray-200 pt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Colonne gauche : Config SMTP */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-[#1e3a8a]" />
                      {t.smtpTitle}
                    </h3>
                    {smtpConfigured && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                        <ShieldCheck className="w-3 h-3" />
                        {t.smtpConfigured}
                      </span>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.smtpHost}</label>
                      <input
                        type="text"
                        value={smtpConfig.host}
                        onChange={(e) => setSmtpConfig(prev => ({ ...prev, host: e.target.value }))}
                        placeholder="smtp.gmail.com"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.smtpPort}</label>
                        <input
                          type="number"
                          value={smtpConfig.port}
                          onChange={(e) => setSmtpConfig(prev => ({ ...prev, port: parseInt(e.target.value) || 587 }))}
                          placeholder="587"
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                        />
                      </div>
                      <div className="flex items-end pb-2.5 gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={smtpConfig.use_tls}
                            onChange={(e) => {
                              const use_tls = e.target.checked
                              setSmtpConfig(prev => ({ 
                                ...prev, 
                                use_tls, 
                                use_ssl: use_tls ? false : prev.use_ssl 
                              }))
                            }}
                            className="w-4 h-4 text-[#1e3a8a] rounded"
                          />
                          <span className="text-sm text-gray-700">{t.smtpUseTls}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={smtpConfig.use_ssl}
                            onChange={(e) => {
                              const use_ssl = e.target.checked
                              setSmtpConfig(prev => ({ 
                                ...prev, 
                                use_ssl, 
                                use_tls: use_ssl ? false : prev.use_tls,
                                port: use_ssl ? 465 : (prev.host.includes('gmail') ? 587 : prev.port)
                              }))
                            }}
                            className="w-4 h-4 text-[#1e3a8a] rounded"
                          />
                          <span className="text-sm text-gray-700">{t.smtpUseSsl}</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.smtpUsername}</label>
                      <input
                        type="email"
                        value={smtpConfig.username}
                        onChange={(e) => setSmtpConfig(prev => ({ ...prev, username: e.target.value }))}
                        placeholder="votre.email@gmail.com"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.smtpPassword}</label>
                      <input
                        type="password"
                        value={smtpConfig.password}
                        onChange={(e) => setSmtpConfig(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="abcdefghijklmnop"
                        className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] ${
                          passwordHasSpaces ? 'border-red-300 bg-red-50' : 'border-gray-200'
                        }`}
                      />
                      {passwordHasSpaces && (
                        <p className="text-xs text-red-600 mt-1 font-medium">
                          ⚠️ {lang === 'fr' 
                            ? "Retirez les espaces ! Le mot de passe d'application est 16 caractères SANS ESPACES." 
                            : "Remove spaces! The App Password is 16 characters WITHOUT SPACES."}
                        </p>
                      )}
                      <p className="text-xs text-amber-600 mt-1">{t.smtpPasswordHelp}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.smtpFromName}</label>
                      <input
                        type="text"
                        value={smtpConfig.from_name}
                        onChange={(e) => setSmtpConfig(prev => ({ ...prev, from_name: e.target.value }))}
                        placeholder="Lycée Bilingue de Baleng"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.smtpFromEmail}</label>
                      <input
                        type="email"
                        value={smtpConfig.from_email}
                        onChange={(e) => setSmtpConfig(prev => ({ ...prev, from_email: e.target.value }))}
                        placeholder="votre.email@gmail.com"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                      />
                      <p className="text-xs text-gray-400 mt-1">{t.smtpGmailHint}</p>
                    </div>

                    <button
                      onClick={saveSmtpConfig}
                      disabled={smtpLoading}
                      className="w-full px-4 py-2.5 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {smtpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {t.smtpSave}
                    </button>
                  </div>
                </div>

                {/* Colonne droite : Test SMTP */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Play className="w-5 h-5 text-[#1e3a8a]" />
                    {t.smtpTest}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.smtpTestEmail}</label>
                      <input
                        type="email"
                        value={smtpTestEmail}
                        onChange={(e) => setSmtpTestEmail(e.target.value)}
                        placeholder="destinataire@exemple.com"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.smtpTestSubject}</label>
                      <input
                        type="text"
                        value={smtpTestSubject}
                        onChange={(e) => setSmtpTestSubject(e.target.value)}
                        placeholder="Test SMTP"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t.smtpTestBody}</label>
                      <textarea
                        value={smtpTestBody}
                        onChange={(e) => setSmtpTestBody(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-[#1e3a8a]"
                      />
                    </div>

                    <button
                      onClick={handleTestSmtp}
                      disabled={smtpTestSending || !smtpTestEmail}
                      className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {smtpTestSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                      {t.smtpTest}
                    </button>

                    <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-500 space-y-1">
                      <p className="font-medium text-gray-700 mb-1">{lang === 'fr' ? 'Configuration actuelle:' : 'Current config:'}</p>
                      <p>Host: {smtpConfig.host}:{smtpConfig.port}</p>
                      <p>TLS: {smtpConfig.use_tls ? 'Oui' : 'Non'} | SSL: {smtpConfig.use_ssl ? 'Oui' : 'Non'}</p>
                      <p>From: {smtpConfig.from_name} &lt;{smtpConfig.from_email || '—'}&gt;</p>
                      <p>User: {smtpConfig.username || '—'}</p>
                      <p>Pass: {smtpConfig.password ? '***' + smtpConfig.password.replace(/\s/g, '').slice(-4) : '—'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== AUTO NOTIFICATIONS TAB ==================== */}
        {activeTab === 'auto' && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#1e3a8a]" />
                  {t.autoTitle}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">{t.autoSubtitle}</p>
              </div>
              <button
                onClick={() => { setEditingRule(null); setShowRuleForm(true) }}
                className="px-3 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t.newRule}
              </button>
            </div>

            {rulesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : notificationRules.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm text-gray-500">{t.noRules}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notificationRules.map(rule => (
                  <div key={rule.id} className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:shadow-sm transition-shadow">
                    <button
                      onClick={() => handleToggleRule(rule)}
                      className="flex-shrink-0"
                    >
                      {rule.is_active ? (
                        <ToggleRight className="w-8 h-8 text-green-500" />
                      ) : (
                        <ToggleLeft className="w-8 h-8 text-gray-300" />
                      )}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900">{rule.name}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          rule.message_type === 'sms' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {rule.message_type.toUpperCase()}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                          {rule.event_type}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {t.recipientType}: <span className="font-medium">{rule.recipient_type}</span>
                        {rule.template_name && <> · Template: <span className="font-medium">{rule.template_name}</span></>}
                        {rule.delay_minutes > 0 && <> · Délai: {rule.delay_minutes}min</>}
                      </p>
                    </div>

                    <div className="flex gap-1">
                      <button
                        onClick={() => { setEditingRule(rule); setShowRuleForm(true) }}
                        className="p-1.5 hover:bg-gray-100 rounded"
                      >
                        <Edit2 className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.id)}
                        className="p-1.5 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{t.previewTitle}</h3>
              <button onClick={() => setShowPreview(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {previewContent.subject && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">{t.subject}</label>
                  <p className="text-sm text-gray-900 mt-1 p-3 bg-gray-50 rounded-lg">{previewContent.subject}</p>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">{t.body}</label>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg whitespace-pre-wrap text-sm text-gray-900">
                  {previewContent.body}
                </div>
              </div>
              {activeTab === 'sms' && (
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-500">{previewContent.sms_length} {t.characters}</span>
                  <span className="text-[#1e3a8a] font-medium">{previewContent.sms_count} SMS</span>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Form Modal */}
      {showTemplateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingTemplate ? 'Modifier le template' : t.newTemplate}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.templateName}</label>
                <input type="text" defaultValue={editingTemplate?.name} id="tmpl-name" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.templateCode}</label>
                <input type="text" defaultValue={editingTemplate?.code} id="tmpl-code" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select defaultValue={editingTemplate?.message_type || 'email'} id="tmpl-type" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.templateCategory}</label>
                <select defaultValue={editingTemplate?.category || 'custom'} id="tmpl-category" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="password_reset">Changement de mot de passe</option>
                  <option value="grade_notification">Notification de notes</option>
                  <option value="absence">Absence</option>
                  <option value="convocation">Convocation</option>
                  <option value="custom">Personnalisé</option>
                </select>
              </div>
              {(!editingTemplate || editingTemplate.message_type === 'email') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.templateSubject}</label>
                  <input type="text" defaultValue={editingTemplate?.subject || ''} id="tmpl-subject" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.templateBody}</label>
                <textarea defaultValue={editingTemplate?.body} id="tmpl-body" rows={5} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.templateVariables}</label>
                <input type="text" defaultValue={editingTemplate?.variables || '[]'} id="tmpl-variables" placeholder='["prenom", "nom", "classe"]' className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setShowTemplateForm(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
                {t.close}
              </button>
              <button
                onClick={() => {
                  const name = (document.getElementById('tmpl-name') as HTMLInputElement)?.value
                  const code = (document.getElementById('tmpl-code') as HTMLInputElement)?.value
                  const message_type = (document.getElementById('tmpl-type') as HTMLSelectElement)?.value
                  const category = (document.getElementById('tmpl-category') as HTMLSelectElement)?.value
                  const subject = (document.getElementById('tmpl-subject') as HTMLInputElement)?.value
                  const body = (document.getElementById('tmpl-body') as HTMLTextAreaElement)?.value
                  const variables = (document.getElementById('tmpl-variables') as HTMLInputElement)?.value
                  handleSaveTemplate({ name, code, message_type, category, subject: subject || null, body, variables })
                }}
                className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e]"
              >
                {t.saveTemplate}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rule Form Modal */}
      {showRuleForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingRule ? 'Modifier la règle' : t.newRule}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.ruleName}</label>
                <input type="text" defaultValue={editingRule?.name} id="rule-name" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.eventType}</label>
                <select defaultValue={editingRule?.event_type || 'grade_entered'} id="rule-event" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="grade_entered">{t.eventGradeEntered}</option>
                  <option value="absence_recorded">{t.eventAbsenceRecorded}</option>
                  <option value="convocation_created">{t.eventConvocationCreated}</option>
                  <option value="discipline_recorded">{t.eventDisciplineRecorded}</option>
                  <option value="payment_received">{t.eventPaymentReceived}</option>
                  <option value="student_registered">{t.eventStudentRegistered}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select defaultValue={editingRule?.message_type || 'sms'} id="rule-msg-type" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                <select defaultValue={editingRule?.template_id || ''} id="rule-template" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="">— Aucun —</option>
                  {templates.map(tmpl => (
                    <option key={tmpl.id} value={tmpl.id}>{tmpl.name} ({tmpl.message_type})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.recipientType}</label>
                <select defaultValue={editingRule?.recipient_type || 'parent'} id="rule-recipient" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="parent">{t.recipientParent}</option>
                  <option value="student">{t.recipientStudent}</option>
                  <option value="teacher">{t.recipientTeacher}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.delayMinutes}</label>
                <input type="number" min={0} defaultValue={editingRule?.delay_minutes || 0} id="rule-delay" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.filterMinGrade}</label>
                <input type="number" min={0} max={20} step={0.5} defaultValue={editingRule?.filters?.min_grade || ''} id="rule-min-grade" placeholder="Ex: 10" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setShowRuleForm(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
                {t.close}
              </button>
              <button
                onClick={() => {
                  const name = (document.getElementById('rule-name') as HTMLInputElement)?.value
                  const event_type = (document.getElementById('rule-event') as HTMLSelectElement)?.value
                  const message_type = (document.getElementById('rule-msg-type') as HTMLSelectElement)?.value
                  const template_id = (document.getElementById('rule-template') as HTMLSelectElement)?.value
                  const recipient_type = (document.getElementById('rule-recipient') as HTMLSelectElement)?.value
                  const delay_minutes = parseInt((document.getElementById('rule-delay') as HTMLInputElement)?.value || '0')
                  const min_grade = (document.getElementById('rule-min-grade') as HTMLInputElement)?.value
                  const filters: any = {}
                  if (min_grade) filters.min_grade = parseFloat(min_grade)

                  const payload = {
                    name,
                    event_type,
                    message_type,
                    template_id: template_id ? parseInt(template_id) : null,
                    recipient_type,
                    delay_minutes,
                    filters: JSON.stringify(filters)
                  }

                  if (editingRule) {
                    handleUpdateRule(editingRule.id, payload)
                  } else {
                    handleCreateRule(payload)
                  }
                }}
                className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e]"
              >
                {editingRule ? 'Mettre à jour' : t.newRule}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}